import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Produit } from 'app/models/produit';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import { ProduitsService } from 'app/services/produits/produits.service';
import { AwscontrolService, UploadResponse, ImageUploadResponse } from 'app/services/aws/awscontrol.service';
import { FormControl } from '@angular/forms';
import Swal from "sweetalert2";
import { HttpErrorResponse } from "@angular/common/http";
import { Location } from '@angular/common';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

type ImageUploadMode = 'direct' | 'imageproduit';

@Component({
  selector: 'app-ajouter-produit',
  templateUrl: './ajouter-produit.component.html',
  styleUrls: ['./ajouter-produit.component.scss']
})
export class AjouterProduitComponent implements OnInit {

  produit: Produit = new Produit();

  // Form Controls
  numCommercialFormControl: FormControl;
  emailCommercialFormControl: FormControl;
  agencySearchControl: FormControl;

  // File handling
  selectedFiles: FileList;
  fileNames: any[] = [];

  // Images
  public image: string = '';
  allAttch: any[] = [];

  // ==================== IMAGE UPLOAD MODE ====================
  imageUploadMode: ImageUploadMode = 'direct';
  previewOriginalUrl: string | null = null;
  previewProcessedUrl: string | null = null;
  isAwaitingValidation: boolean = false;

  // Data
  public materiaux: any;
  agenciesApp: any;
  idAgency: any;
  NameAgence: any;
  selectedAgencyId: string = '';
  filteredAgencies: Observable<any[]>;

  // Loading state
  isUploading: boolean = false;
  isSubmitting: boolean = false;

  // Language support
  agencyAvailableLanguages: string[] = [];
  showEnglishFields: boolean = false;
  showArabicFields: boolean = false;

  constructor(
    private router: Router,
    private agenciesService: AgenciesService,
    private produitService: ProduitsService,
    private awsService: AwscontrolService,
    private location: Location
  ) {
    this.numCommercialFormControl = new FormControl('');
    this.emailCommercialFormControl = new FormControl('');
    this.agencySearchControl = new FormControl('');
  }

  ngOnInit(): void {
    this.getData();
    this.idAgency = this.agenciesService.getIdAgency();
    if (this.idAgency) {
      this.AgencyNameById();
    }

    this.filteredAgencies = this.agencySearchControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterAgencies(value))
    );
  }

  private _filterAgencies(value: string): any[] {
    const filterValue = value ? value.toLowerCase() : '';
    return this.materiaux ? this.materiaux.filter(agency =>
      agency.name.toLowerCase().includes(filterValue)
    ) : [];
  }

  displayAgency(agency: any): string {
    return agency ? agency.name : '';
  }

  onAgencySelected(event: any): void {
    const selectedAgency = event.option.value;
    if (selectedAgency) {
      this.NameAgence = selectedAgency.name;
      this.selectedAgencyId = selectedAgency.id;

      if (selectedAgency.availableLanguages && Array.isArray(selectedAgency.availableLanguages)) {
        this.agencyAvailableLanguages = selectedAgency.availableLanguages.map((l: string) => l.toUpperCase());
        this.showEnglishFields = this.agencyAvailableLanguages.includes('EN');
        this.showArabicFields  = this.agencyAvailableLanguages.includes('AR');
      } else {
        this.agencyAvailableLanguages = ['FR'];
        this.showEnglishFields = false;
        this.showArabicFields  = false;
      }
    }
  }

  getData(): void {
    this.getAllAgencies();
    this.agenciesService.getList().subscribe((data) => {
      this.materiaux = data;
    });
  }

  getAllAgencies(): void {
    this.agenciesService.getList().subscribe((data) => {
      this.agenciesApp = data;
    });
  }

  AgencyNameById(): void {
    this.agenciesService.getAgencieById(this.idAgency).subscribe((data: any) => {
      this.NameAgence = data.name;
      this.selectedAgencyId = data.id;
      this.agencySearchControl.setValue(data.name);
      if (data.availableLanguages && Array.isArray(data.availableLanguages)) {
        this.agencyAvailableLanguages = data.availableLanguages.map((l: string) => l.toUpperCase());
        this.showEnglishFields = this.agencyAvailableLanguages.includes('EN');
        this.showArabicFields  = this.agencyAvailableLanguages.includes('AR');
      }
    });
  }

  // ============================================================
  // ==================== IMAGE UPLOAD ==========================
  // ============================================================

  setUploadMode(mode: ImageUploadMode): void {
    if (this.isUploading) return;
    if (this.isAwaitingValidation) {
      this.cancelImagePreview(true);
    }
    this.imageUploadMode = mode;
    console.log('🔄 Mode upload image :', mode);
  }

  private validateImageFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;
    if (!validTypes.includes(file.type)) {
      Swal.fire({
        icon: 'warning',
        title: 'Format non supporté',
        text: 'JPEG, PNG, JPG, GIF, WEBP uniquement',
        confirmButtonColor: '#ff9800'
      });
      return false;
    }
    if (file.size > maxSize) {
      Swal.fire({
        icon: 'warning',
        title: 'Fichier trop volumineux',
        text: 'Max 5MB',
        confirmButtonColor: '#ff9800'
      });
      return false;
    }
    return true;
  }

  selectFileimg(e: any): void {
    const input = e.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    if (!this.validateImageFile(file)) {
      input.value = '';
      return;
    }

    this.selectedFiles = input.files;

    // Aperçu local de l'original
    const reader = new FileReader();
    reader.onload = (ev) => {
      this.previewOriginalUrl = (ev.target?.result as string) || null;
    };
    reader.readAsDataURL(file);

    if (this.imageUploadMode === 'direct') {
      this.uploadDirect(file, input);
    } else {
      this.uploadViaImageproduit(file, input);
    }
  }

  private uploadDirect(file: File, input: HTMLInputElement): void {
    this.isUploading = true;
    const folder = 'produits';

    this.awsService.uploadFile(file, folder).subscribe({
      next: (response: UploadResponse) => {
        this.isUploading = false;
        if (response.success && response.urls && response.urls.length > 0) {
          const uploadedUrl = response.urls[0];
          localStorage.setItem('prodImg', JSON.stringify(uploadedUrl));
          this.allAttch.push(uploadedUrl);
          this.image = uploadedUrl;
          this.previewOriginalUrl = null;

          Swal.fire({
            icon: 'success',
            title: 'Image téléchargée',
            timer: 1500,
            showConfirmButton: false
          });
          input.value = '';
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Erreur',
            text: response.error || "Échec du téléchargement de l'image",
          });
          this.previewOriginalUrl = null;
        }
      },
      error: () => {
        this.isUploading = false;
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: "Erreur lors du téléchargement de l'image",
        });
        this.previewOriginalUrl = null;
      }
    });
  }

  private uploadViaImageproduit(file: File, input: HTMLInputElement): void {
    this.isUploading = true;
    this.isAwaitingValidation = false;
    this.previewProcessedUrl = null;

    const folder = 'produits';

    this.awsService.imageproduit([file], folder).subscribe({
      next: (response: ImageUploadResponse) => {
        this.isUploading = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.previewProcessedUrl = response.urls[0];
          this.isAwaitingValidation = true;
          input.value = '';
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Erreur',
            text: (response.errors && response.errors[0]) || "Échec du traitement de l'image",
          });
          this.previewOriginalUrl = null;
        }
      },
      error: () => {
        this.isUploading = false;
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: "Impossible de traiter l'image",
        });
        this.previewOriginalUrl = null;
      }
    });
  }

  validateImagePreview(): void {
    if (!this.previewProcessedUrl) return;

    localStorage.setItem('prodImg', JSON.stringify(this.previewProcessedUrl));
    this.allAttch.push(this.previewProcessedUrl);
    this.image = this.previewProcessedUrl;

    this.previewOriginalUrl = null;
    this.previewProcessedUrl = null;
    this.isAwaitingValidation = false;
    this.selectedFiles = null as any;

    Swal.fire({
      icon: 'success',
      title: 'Image validée',
      text: 'L\'image a été acceptée pour ce produit.',
      timer: 1500,
      showConfirmButton: false
    });
  }

  cancelImagePreview(silent: boolean = false): void {
    const urlToDelete = this.previewProcessedUrl;

    const doCancel = () => {
      if (urlToDelete) {
        this.awsService.deleteFile(urlToDelete).subscribe({
          next: () => console.log('🗑️ Image refusée supprimée du S3'),
          error: (err) => console.warn('⚠️ Impossible de supprimer du S3 :', err)
        });
      }
      this.previewOriginalUrl = null;
      this.previewProcessedUrl = null;
      this.isAwaitingValidation = false;
      this.selectedFiles = null as any;
    };

    if (silent) {
      doCancel();
      return;
    }

    Swal.fire({
      title: 'Refuser cette image ?',
      text: "L'image traitée sera supprimée et vous pourrez en choisir une autre.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, refuser',
      cancelButtonText: 'Annuler'
    }).then(result => {
      if (result.isConfirmed) doCancel();
    });
  }

  // No validation – always returns true
  validateForm(): boolean {
    return true;
  }

  // Optional video URL – no validation
  validateVideoUrl(): boolean {
    return true;
  }

  // ==================== SUBMIT ====================
  onSubmit(): void {
    if (this.isAwaitingValidation) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation requise',
        text: "Veuillez valider ou refuser l'image avant de continuer.",
        confirmButtonColor: '#ff9800'
      });
      return;
    }

    if (this.isUploading) {
      Swal.fire({
        icon: "warning",
        title: "Téléchargement en cours",
        text: "Veuillez attendre la fin du téléchargement de l'image",
      });
      return;
    }

    this.isSubmitting = true;

    // Convert categoryList array to comma-separated string
    const categoryString = (this.produit.categoryList && this.produit.categoryList.length > 0)
      ? this.produit.categoryList.join(',')
      : '';

    // Get image from either the uploaded variable or localStorage
    let imageUrl = this.image;
    if (!imageUrl) {
      try {
        const stored = localStorage.getItem('prodImg');
        if (stored) imageUrl = JSON.parse(stored);
      } catch (e) { /* ignore */ }
    }

    const produits = {
      name: this.produit.name || '',
      nomarab: this.produit.nomarab || '',
      nomanglais: this.produit.nomanglais || '',
      description: this.produit.description || '',
      descriptionAnglais: this.produit.descriptionAnglais || '',
      prix: this.produit.prix || '',
      agencyName: this.NameAgence || '',
      categoryList: categoryString,
      imageUrl: imageUrl || '',
      videoUrlProduit: this.produit.videoUrlProduit || '',
      nomCommercial: this.produit.nomCommercial || '',
      numCommercial: this.numCommercialFormControl.value || '',
      emailCommercial: this.emailCommercialFormControl.value || ''
    };

    console.log('📦 Payload sent:', JSON.stringify(produits));

    this.produitService.createProduit(produits).subscribe(
      () => {
        this.isSubmitting = false;
        Swal.fire({
          icon: "success",
          title: "Succès",
          text: "Produit créé avec succès",
          timer: 2000
        });

        setTimeout(() => {
          if (this.selectedAgencyId) {
            this.router.navigate(['/list-produits', this.selectedAgencyId]);
          } else {
            this.navigateBack();
          }
        }, 500);
      },
      (error: HttpErrorResponse) => {
        this.isSubmitting = false;
        console.error("Error:", error);

        let errorMessage = "Une erreur s'est produite lors de la création du produit.";
        if (error.error && typeof error.error === 'string') {
          errorMessage = error.error;
        } else if (error.error && error.error.message) {
          errorMessage = error.error.message;
        }

        Swal.fire({
          icon: "error",
          title: "Erreur",
          text: errorMessage,
        });
      }
    );
  }

  navigateBack(): void {
    this.location.back();
  }
}